import BaseWidget, { escapeHtml } from './baseWidget.js';
import stammdatenService from '../../services/stammdatenService.js';
import basedataService from '../../services/basedataService.js';
import dayjs from '../../../../../shared/services/dayjs.js';
import { getAktuellerZeitraum } from '../../util.js';

const FILTER_TYPES = [
	{ value: 'all', label: 'Alle', title: 'Alle Fristen'},
	{ value: 'Probezeit', label: 'Probezeit', title: 'Probezeit Ende'},
	{ value: 'Befristung', label: 'Befristung', title: 'Befristung Ende'},
	{ value: 'Aufenthaltsgenehmigung', label: 'Aufenthaltsgenehmigung', title: 'Aufenthaltsgenehmigung Ende'},
	{ value: 'Arbeitserlaubnis', label: 'Arbeitserlaubnis', title: 'Arbeitserlaubnis Ende'},
	{ value: 'Immatrikulationsbescheinigung', label: 'Immatrikulation', title: 'Immatrikulationsbesŝcheinigung Ende	'},
	{ value: 'Kurzfristigkeit', label: 'Kurzfristigkeit', title: 'Kurzfristigkeit Ende'},
	{ value: 'InfektionsschutzErstbelehrung', label: 'IfSG Erstbelehrung', title: 'Infektionsschutz Erstbelehrung' },
	{ value: 'InfektionsschutzFolgebelehrung', label: 'IfSG Folgebelehrung', title: 'Infektionsschutz Folgebelehrung' },
];

// Mapping Fristen-Typ → aria-label des zugehörigen Input-Felds auf der Beschäftigung-Seite.
const TYPE_TO_FIELD = {
	Probezeit: 'ma-ProbezeitBis',
	Befristung: 'ma-BefristetBis',
	Aufenthaltsgenehmigung: 'ma-AufenthaltsgenehmigungBis',
	Arbeitserlaubnis: 'ma-ArbeitserlaubnisBis',
	Immatrikulationsbescheinigung: 'ma-Immatrikulationsbescheinigung',
	Kurzfristigkeit: 'ma-Personengruppenschluessel',
	InfektionsschutzErstbelehrung: 'ma-InfektionsschutzErstbelehrung',
	InfektionsschutzFolgebelehrung: 'ma-InfektionsschutzFolgebelehrung',
};

// Ausgeschriebene Bezeichnung für die Listenanzeige (Filter-Chips nutzen die kürzere IfSG-Abkürzung).
const TYPE_LABELS = {
	InfektionsschutzErstbelehrung: 'Infektionsschutz Erstbelehrung',
	InfektionsschutzFolgebelehrung: 'Infektionsschutz Folgebelehrung',
};

/**
 * Listet bevorstehende Fristen und Ablaufdaten aller Mitarbeiter auf.
 * Zeigt: Mitarbeitername, Fristentyp, verbleibende Tage.
 */
export default class DeadlinesWidget extends BaseWidget {
	constructor(type, instanceId, state = {}) {
		super(type, instanceId, state);
		// Filter-Typ wird absichtlich nicht persistiert: wenn beim nächsten Laden keine
		// passenden Fristen vorhanden sind, würde das Widget leer wirken obwohl andere
		// Typen relevant wären. Beim Öffnen also immer "Alle" zeigen.
		this.filter = 'all';
		this.hideMissing = state.hideMissing === true;
		this.disabledTypes = new Set(Array.isArray(state.disabledTypes) ? state.disabledTypes : []);
		this._allDeadlines = [];
		this._allDeadlinesRaw = [];
	}

	serializeState() {
		return { hideMissing: this.hideMissing, disabledTypes: Array.from(this.disabledTypes) };
	}

	async onRender() {
		this.setLoading();
		const zeitraum = getAktuellerZeitraum();
		const [malist, laender] = await Promise.all([
			stammdatenService.holeMitarbeiterListe(
				stammdatenService.aktuelleBetriebsstaette,
				zeitraum,
			),
			basedataService.holeLaenderBasedataProvider(),
		]);
		const euNationenschluessel = new Set(
			laender.filter((l) => l.EgMitglied == '1').map((l) => l.Nationenschluessel),
		);

		const heute = dayjs().startOf('day');
		const deadlines = [];

		// Sammle alle Fristen aller Mitarbeiter
		malist.forEach((ma) => {
			const austrittMA = ma.Austrittsdatum
				? dayjs(ma.Austrittsdatum)
				: null;

			// Ignoriere Mitarbeiter, die bereits ausgetreten sind
			if (austrittMA && heute.isAfter(austrittMA)) return;
			// Ignorie Mitarbeiter, die archiviert sind
			if (ma.Archiviert) return;

			const maName = `${ma.Nachname}, ${ma.Vorname}`;

			// Probezeitbis (nach Ablauf nicht mehr relevant)
			if (ma.ProbezeitBis) {
				const fristDatum = dayjs(ma.ProbezeitBis);
				if (fristDatum.isValid()) {
					const daysRemaining = heute.diff(fristDatum, 'days') * -1;
					if (daysRemaining >= 0 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Probezeit',
							daysRemaining,
							fristDatum,
						});
					}
				}
			}

			// BefristetBis (befristeter Arbeitsvertrag)
			if (ma.BefristetBis) {
				const fristDatum = dayjs(ma.BefristetBis);
				if (fristDatum.isValid()) {
					const daysRemaining = heute.diff(fristDatum, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Befristung',
							daysRemaining,
							fristDatum,
						});
					}
				}
			}

			// Aufenthalts- und Arbeitsgenehmigung (nur für Nicht-EU-Bürger)
			if (ma.Staatsangehoerigkeit) {
				const isNonEU = !euNationenschluessel.has(ma.Staatsangehoerigkeit);
				if (isNonEU) {
					const aufenthaltFrist = ma.AufenthaltsgenehmigungBis ? dayjs(ma.AufenthaltsgenehmigungBis) : null;
					if (!aufenthaltFrist || !aufenthaltFrist.isValid()) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Aufenthaltsgenehmigung',
							missing: true,
						});
					} else {
						const daysRemaining = heute.diff(aufenthaltFrist, 'days') * -1;
						if (daysRemaining >= -90 && daysRemaining <= 90) {
							deadlines.push({
								maName,
								maId: ma._id,
								type: 'Aufenthaltsgenehmigung',
								daysRemaining,
								fristDatum: aufenthaltFrist,
							});
						}
					}

					const arbeitserlaubnisFrist = ma.ArbeitserlaubnisBis ? dayjs(ma.ArbeitserlaubnisBis) : null;
					if (!arbeitserlaubnisFrist || !arbeitserlaubnisFrist.isValid()) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Arbeitserlaubnis',
							missing: true,
						});
					} else {
						const daysRemaining = heute.diff(arbeitserlaubnisFrist, 'days') * -1;
						if (daysRemaining >= -90 && daysRemaining <= 90) {
							deadlines.push({
								maName,
								maId: ma._id,
								type: 'Arbeitserlaubnis',
								daysRemaining,
								fristDatum: arbeitserlaubnisFrist,
							});
						}
					}
				}
			}

			// Kurzfristige Beschäftigung (PGS 110): zwei Limits
			// - 70 Arbeitstage pro Kalenderjahr (Uebersicht.KurzfristigTage)
			// - max 10 Monate durchgehende Beschäftigung ab Eintritt
			// Beide werden separat angezeigt, damit klar ist welches Limit greift.
			if (ma.PGS === '110') {
				const kurzfristigTage = ma.KurzfristigTage;
				if (typeof kurzfristigTage === 'number') {
					const tageRest = 70 - kurzfristigTage;
					// Erst ab Annäherung an die Grenze (oder Überschreitung) anzeigen
					if (tageRest <= 30) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Kurzfristigkeit',
							subtype: '70-Tage-Grenze',
							daysRemaining: tageRest,
							daysText: `${kurzfristigTage} / 70 Tage`,
						});
					}
				}
				if (ma.Eintrittsdatum) {
					const grenze = dayjs(ma.Eintrittsdatum).add(10, 'months');
					if (grenze.isValid()) {
						const daysRemaining = grenze.diff(heute, 'days');
						if (daysRemaining <= 60) {
							deadlines.push({
								maName,
								maId: ma._id,
								type: 'Kurzfristigkeit',
								subtype: '10-Monats-Grenze',
								daysRemaining,
								fristDatum: grenze,
							});
						}
					}
				}
			}

			// Immatrikulationsbescheinigung (Studenten)
			if (ma.PGS === '106') {
				const immatrikulationFrist = ma.Immatrikulationsbescheinigung ? dayjs(ma.Immatrikulationsbescheinigung) : null;
				if (!immatrikulationFrist || !immatrikulationFrist.isValid()) {
					deadlines.push({
						maName,
						maId: ma._id,
						type: 'Immatrikulationsbescheinigung',
						missing: true,
					});
				} else {
					const daysRemaining = heute.diff(immatrikulationFrist, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'Immatrikulationsbescheinigung',
							daysRemaining,
							fristDatum: immatrikulationFrist,
						});
					}
				}
			}

			// Infektionsschutzbelehrung: Erstbelehrung ist einmalig (nur Vorhandensein prüfen).
			// Die Folgebelehrung ist nur relevant, wenn die Erstbelehrung vorliegt – dann muss
			// sie aber ausgefüllt sein (sonst "fehlt") und gilt, sobald ausgefüllt, jeweils ein
			// Jahr ab ihrem Datum.
			const erstbelehrungFrist = ma.InfektionsschutzErstbelehrung ? dayjs(ma.InfektionsschutzErstbelehrung) : null;
			const hatErstbelehrung = erstbelehrungFrist && erstbelehrungFrist.isValid();
			if (!hatErstbelehrung) {
				deadlines.push({
					maName,
					maId: ma._id,
					type: 'InfektionsschutzErstbelehrung',
					missing: true,
				});
			} else {
				const folgebelehrungFrist = ma.InfektionsschutzFolgebelehrung ? dayjs(ma.InfektionsschutzFolgebelehrung) : null;
				if (!folgebelehrungFrist || !folgebelehrungFrist.isValid()) {
					deadlines.push({
						maName,
						maId: ma._id,
						type: 'InfektionsschutzFolgebelehrung',
						missing: true,
					});
				} else {
					const ablaufDatum = folgebelehrungFrist.add(1, 'year');
					const daysRemaining = heute.diff(ablaufDatum, 'days') * -1;
					if (daysRemaining >= -90 && daysRemaining <= 90) {
						deadlines.push({
							maName,
							maId: ma._id,
							type: 'InfektionsschutzFolgebelehrung',
							daysRemaining,
							fristDatum: ablaufDatum,
						});
					}
				}
			}
		});

		// Sortiere: fehlende Daten zuerst, dann nach verbleibenden Tagen aufsteigend
		deadlines.sort((a, b) => {
			if (a.missing && !b.missing) return -1;
			if (!a.missing && b.missing) return 1;
			return a.daysRemaining - b.daysRemaining;
		});

		this._allDeadlinesRaw = deadlines;
		this._applyTypeFilter();

		this.bodyEl.innerHTML = `
			<div class="widget-deadline-filter"></div>
			<div class="widget-deadline-list"></div>
		`;
		this.renderFilterChips();
		this.renderList();
		this._installHeaderSettings();
	}

	/**
	 * Wendet die per Einstellungen-Menü deaktivierten Fristentypen auf die
	 * zuletzt geladenen Rohdaten an. Getrennt von onRender(), damit das
	 * Ein-/Ausblenden einzelner Checks ohne erneuten Server-Roundtrip geht.
	 */
	_applyTypeFilter() {
		this._allDeadlines = this._allDeadlinesRaw.filter((d) => !this.disabledTypes.has(d.type));
	}

	/**
	 * Hängt einen Einstellungen-Button in die Tile-Kopfzeile (neben Refresh/Close),
	 * über den einzelne Fristen-Checks dauerhaft ausgeblendet werden können – nötig,
	 * weil z.B. eine fehlende IfSG-Erstbelehrung sonst für jeden MA ohne Datum auftaucht
	 * und die Liste zumüllen kann. BaseWidget bietet bewusst keinen Header-Hook, deshalb
	 * DOM-Injection (siehe LohnkostenTrendWidget._installHeaderToggle für das Vorbild).
	 */
	_installHeaderSettings() {
		const header = this.bodyEl.closest('.dashboard-widget')?.querySelector('.dashboard-widget-header');
		if (!header) return;
		const actions = header.querySelector('.dashboard-widget-actions');
		if (!actions || actions.querySelector('.widget-deadline-settings-btn')) return;
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'widget-deadline-settings-btn';
		btn.title = 'Angezeigte Fristen auswählen';
		btn.setAttribute('aria-label', 'widget-deadline-settings');
		btn.innerHTML = '<i class="bi bi-sliders"></i>';
		btn.addEventListener('click', (e) => {
			e.stopPropagation();
			this._toggleSettingsPanel(btn);
		});
		actions.insertBefore(btn, actions.firstChild);
	}

	_toggleSettingsPanel(anchorBtn) {
		if (this._settingsPanel) {
			this._closeSettingsPanel();
			return;
		}
		const panel = document.createElement('div');
		panel.className = 'widget-deadline-settings-panel';
		panel.innerHTML = `
			<div class="widget-deadline-settings-title">Angezeigte Fristen</div>
			${FILTER_TYPES.filter((f) => f.value !== 'all').map((f) => `
				<label class="widget-deadline-settings-row" title="${escapeHtml(f.title || f.label)}">
					<input type="checkbox" data-type="${escapeHtml(f.value)}" ${this.disabledTypes.has(f.value) ? '' : 'checked'}>
					<span>${escapeHtml(f.label)}</span>
				</label>
			`).join('')}
		`;
		document.body.appendChild(panel);
		const rect = anchorBtn.getBoundingClientRect();
		panel.style.top = `${rect.bottom + 4}px`;
		panel.style.right = `${window.innerWidth - rect.right}px`;
		panel.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
			checkbox.addEventListener('change', () => {
				const value = checkbox.dataset.type;
				if (checkbox.checked) {
					this.disabledTypes.delete(value);
				} else {
					this.disabledTypes.add(value);
				}
				this._applyTypeFilter();
				// Falls der aktuell gewählte Filter-Chip gerade ausgeblendet wurde, zurück auf "Alle".
				if (this.filter !== 'all' && this.disabledTypes.has(this.filter)) {
					this.filter = 'all';
				}
				this.renderFilterChips();
				this.renderList();
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		});
		this._settingsPanel = panel;
		this._settingsOutsideHandler = (e) => {
			if (panel.contains(e.target) || anchorBtn.contains(e.target)) return;
			this._closeSettingsPanel();
		};
		this._settingsKeyHandler = (e) => {
			if (e.key === 'Escape') this._closeSettingsPanel();
		};
		document.addEventListener('mousedown', this._settingsOutsideHandler);
		document.addEventListener('keydown', this._settingsKeyHandler);
	}

	_closeSettingsPanel() {
		if (!this._settingsPanel) return;
		this._settingsPanel.remove();
		this._settingsPanel = null;
		document.removeEventListener('mousedown', this._settingsOutsideHandler);
		document.removeEventListener('keydown', this._settingsKeyHandler);
		this._settingsOutsideHandler = null;
		this._settingsKeyHandler = null;
	}

	destroy() {
		this._closeSettingsPanel();
	}

	renderFilterChips() {
		const filterEl = this.bodyEl.querySelector('.widget-deadline-filter');
		if (!filterEl) return;
		const availableTypes = new Set(this._allDeadlines.map((d) => d.type));
		const hasMissing = this._allDeadlines.some((d) => d.missing);
		const typeChips = FILTER_TYPES
			.filter((f) => f.value === 'all' || availableTypes.has(f.value))
			.sort((a, b) => {
				if (a.value === 'all') return -1;
				if (b.value === 'all') return 1;
				return a.label.localeCompare(b.label, 'de');
			})
			.map((f) => `
				<button type="button" class="deadline-chip${this.filter === f.value ? ' active' : ''}" data-filter="${escapeHtml(f.value)}"${f.title ? ` title="${escapeHtml(f.title)}"` : ''}>
					${escapeHtml(f.label)}
				</button>
			`).join('');
		// Toggle nur sichtbar wenn es überhaupt fehlende Werte gibt.
		// Das Fragezeichen-Icon spiegelt die "fehlt"-Pille in den Listeneinträgen,
		// daneben deutet das Augen-Icon an, ob diese gerade ein- oder ausgeblendet sind.
		const missingToggle = hasMissing
			? `<button type="button" class="deadline-chip deadline-chip-toggle${this.hideMissing ? ' active' : ''}" data-toggle="hideMissing" title="${this.hideMissing ? 'Fehlende Werte einblenden' : 'Fehlende Werte ausblenden'}" aria-label="Fehlende Werte ausblenden">
				<i class="bi ${this.hideMissing ? 'bi-eye-slash' : 'bi-eye'}"></i>
				<i class="bi bi-question-circle"></i>
			</button>`
			: '';
		filterEl.innerHTML = typeChips + missingToggle;
		filterEl.querySelectorAll('.deadline-chip:not(.deadline-chip-toggle)').forEach((chip) => {
			chip.addEventListener('click', () => {
				this.filter = chip.dataset.filter;
				filterEl.querySelectorAll('.deadline-chip:not(.deadline-chip-toggle)').forEach((c) => c.classList.toggle('active', c.dataset.filter === this.filter));
				this.renderList();
				// Persistiere ausgewählten Filter
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		});
		const toggleEl = filterEl.querySelector('.deadline-chip-toggle');
		if (toggleEl) {
			toggleEl.addEventListener('click', () => {
				this.hideMissing = !this.hideMissing;
				this.renderFilterChips();
				this.renderList();
				if (window.myServices?.dashboard) {
					window.myServices.dashboard.saveConfig();
				}
			});
		}
	}

	renderList() {
		const listEl = this.bodyEl.querySelector('.widget-deadline-list');
		if (!listEl) return;

		let filtered = this.filter === 'all'
			? this._allDeadlines
			: this._allDeadlines.filter((d) => d.type === this.filter);
		if (this.hideMissing) {
			filtered = filtered.filter((d) => !d.missing);
		}

		if (filtered.length === 0) {
			listEl.innerHTML = '<div class="widget-empty">Keine bevorstehenden Fristen.</div>';
			return;
		}

		listEl.innerHTML = '';
		filtered.forEach(({ maName, maId, type, subtype, daysRemaining, daysText: customDaysText, missing }) => {
			const item = document.createElement('div');
			item.className = 'widget-deadline-item';
			item.addEventListener('click', async () => {
				await window.myHandlers.selectMA(maId, 'Beschäftigung');
				const fieldLabel = TYPE_TO_FIELD[type];
				if (fieldLabel) {
					const input = document.querySelector(`[aria-label="${fieldLabel}"]`);
					if (input) {
						input.scrollIntoView({ behavior: 'smooth', block: 'center' });
						input.classList.add('field-highlight');
						setTimeout(() => input.classList.remove('field-highlight'), 3000);
					}
				}
			});

			let urgencyClass;
			let daysText;
			let daysIcon = '';
			if (missing) {
				urgencyClass = 'deadline-expired';
				daysText = 'fehlt';
				daysIcon = '<i class="bi bi-question-circle"></i> ';
			} else if (daysRemaining < 0) {
				urgencyClass = 'deadline-expired';
				daysText = customDaysText || `−${Math.abs(daysRemaining)} Tag${Math.abs(daysRemaining) === 1 ? '' : 'e'}`;
				daysIcon = '<i class="bi bi-hourglass-bottom"></i> ';
			} else if (daysRemaining < 7) {
				urgencyClass = 'deadline-urgent';
				daysText = customDaysText || `${daysRemaining} Tag${daysRemaining === 1 ? '' : 'e'}`;
			} else if (daysRemaining < 30) {
				urgencyClass = 'deadline-soon';
				daysText = customDaysText || `${daysRemaining} Tag${daysRemaining === 1 ? '' : 'e'}`;
			} else {
				urgencyClass = 'deadline-ok';
				daysText = customDaysText || `${daysRemaining} Tag${daysRemaining === 1 ? '' : 'e'}`;
			}

			const typeLabel = TYPE_LABELS[type] || type;
			const typeLine = subtype ? `${typeLabel} · ${subtype}` : typeLabel;
			item.innerHTML = `
				<div class="deadline-content">
					<div class="deadline-ma">${escapeHtml(maName)}</div>
					<div class="deadline-type">${escapeHtml(typeLine)}</div>
				</div>
				<div class="deadline-days ${urgencyClass}">${daysIcon}${escapeHtml(daysText)}</div>
			`;
			listEl.appendChild(item);
		});
	}
}
